// Dashboard — money screen.
// Phase 4.1: 4 KPI cards + 6-month trend chart.
// Phase 4.2: category donut, budget alerts, recent 5 transactions.
// Phase 7+: category filter chips — filters KPI / trend / breakdown / alerts / recent.
// Phase 8: range mode (month/year) + dark mode support.
// Phase 30: day range mode — filter KPIs/trend by single day.
// Phase 31.3: pull-to-refresh on Dashboard.
// Phase 31.6: browser notification on critical credit-card grace periods.
//
// Phase 35g: aggregations run locally over IndexedDB. The server is
// never touched during a dashboard render — all KPI/trend/breakdown/
// alerts/top/net worth math happens in localDashboard.js.
import React, { useEffect, useState, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { data as ds } from '../lib/dataService.js';
import { computeDashboard } from '../lib/localDashboard.js';
import { formatMoney, formatPercent } from '../lib/format.js';
import usePullToRefresh from '../lib/usePullToRefresh.js';
import notifications from '../lib/notifications.js';
import useTxSync from '../lib/txSync.js';
import { useI18n } from '../i18n/index.jsx';
import { categoryLabel } from '../lib/categories.js';
import KpiCard from '../components/KpiCard.jsx';
import { Money, usePrivacy } from '../lib/privacy.jsx';
import { DashboardTrendChart, DashboardCategoryDonut } from '../components/DashboardCharts.jsx';
import BudgetAlerts from '../components/BudgetAlerts.jsx';
import GoalsSummary from '../components/GoalsSummary.jsx';
import CashFlowForecast from '../components/CashFlowForecast.jsx';
import RecentTransactions from '../components/RecentTransactions.jsx';
import AccountBalancesWidget from '../components/widgets/AccountBalancesWidget.jsx';
import InstallmentsWidget from '../components/widgets/InstallmentsWidget.jsx';
import { db, installmentsRepoMockable as installmentsRepo } from '../lib/localDb.js';
import { useFilter } from '../lib/filterContext.js';
import { useTopBarRightSlot } from '../lib/topbar-slot.jsx';
// Phase 208 / card 208-A3: the semantics of the global opt-in live in
// card 208-B4's module, so this effect and the Settings toggle (208-A2)
// cannot disagree about what "on" means.
import { isRecurringReminderEnabled } from '../lib/recurringReminderSetting.js';

// Phase 208 / card 208-A3 — attach each recurring rule's CATEGORY NAME.
//
// `recurringRepo.list()` returns the raw Dexie rows: they carry
// `category_id` but NOT `category_name`. The server JOINs the name
// (`server/recurring.js`), and `pullFromServer` happens to persist the
// server's `category_name` onto the local row — but a rule created
// LOCALLY has never been to the server, so its name is missing exactly
// for the users the reminder is written for.
//
// The recurring-reminder notification names the category
// (`notifications.maybeFireRecurringReminder` reads `rule.category_name`),
// so the dashboard joins the name locally. This is a LOCAL join — no
// server round-trip (AGENTS.md rule 4).
//
// The key can be EITHER namespace: a locally created rule stores the
// category's `local_uuid`, while a rule that came from the server stores
// the numeric `server_id`. The lookup map is therefore keyed by every
// id a category carries.
function withCategoryNames(rules, categories) {
  if (!Array.isArray(rules) || rules.length === 0) return Array.isArray(rules) ? rules : [];
  const byId = new Map();
  for (const c of categories || []) {
    for (const key of [c.local_uuid, c.server_id, c.id]) {
      if (key != null) byId.set(String(key), c);
    }
  }
  return rules.map(r => {
    const key = r.category_id != null ? r.category_id : r.category_local_uuid;
    const cat = key != null ? byId.get(String(key)) : null;
    // A rule whose category is missing keeps whatever name it already
    // had (possibly null) — the notification then names nothing rather
    // than naming the WRONG thing.
    if (cat) return { ...r, category_name: cat.name };
    return { ...r, category_name: r.category_name ?? null };
  });
}

// Phase 208 / card 208-A3 — drop the rules that were JUST auto-applied
// this session from the "coming" reminder.
//
// WHY
// ---
// `applyPending()` inserts the transaction but does NOT deactivate the
// rule, so a rule that was auto-applied a moment ago is still in the
// rule list and still inside its lead window (it is due today, so
// `daysUntil === 0`). Without this filter the SAME rule is announced
// twice: once by the "coming" toast ("Due today") and once by the
// "added" toast ("X was added automatically"). The phase spec §5.2
// chose option (b) — notify a just-applied rule — but as a SEPARATE
// type, not IN ADDITION to the "coming" nudge.
//
// There is a second-order reason: the "coming" toast consumes the
// ISO-week dedup key (`notifications.js` -> `setLastRecurringNudgeKey`)
// once it fires. If a just-applied rule occupies the toast, it also
// burns the week's key, and a GENUINELY future rule that enters its
// window later that week is silenced for the rest of the week.
//
// `applyPending()` returns `{ recurring_local_uuid, ... }` per inserted
// row, so the applied rules are identifiable by uuid.
function withoutJustApplied(rules, applied) {
  if (!Array.isArray(rules) || rules.length === 0) return Array.isArray(rules) ? rules : [];
  if (!Array.isArray(applied) || applied.length === 0) return rules;
  const appliedUuids = new Set(
    applied
      .map(a => a && a.recurring_local_uuid)
      .filter(u => u != null)
      .map(u => String(u)),
  );
  if (appliedUuids.size === 0) return rules;
  return rules.filter(r => {
    const uuid = r && r.local_uuid;
    if (uuid == null) return true;
    return !appliedUuids.has(String(uuid));
  });
}

export default function Dashboard() {
  const { t } = useI18n();
  const { enabled: privacyEnabled } = usePrivacy();
  // Phase 94: TopBar right-cluster slot via Context. Migrated from
  // the inline IIFE + document.getElementById pattern (which raced
  // with TopBar's commit on cold load of /transactions). Same code
  // path now used by both Dashboard and Transactions.
  const topbarSlot = useTopBarRightSlot();
  const [currency, setCurrency] = useState('HKD');
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Phase 75: dashboard filter drawer — supplementary to the always-
  // visible FilterBar (range/period/category). Drawer bundles the
  // "advanced" filters: type (income/expense/all) + amount range.
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'income' | 'expense'
  const [amountMin, setAmountMin] = useState('');     // optional, '' = no min
  const [amountMax, setAmountMax] = useState('');     // optional, '' = no max
  // Phase 76b: Categories moved from FilterBar chip row into the drawer.
  // Multi-select (Set of category ids) — empty set = all categories.
  const [catsFilter, setCatsFilter] = useState(() => new Set());
  // Count of active advanced filters (for the [+N] badge on the button).
  const advancedActiveCount = (typeFilter !== 'all' ? 1 : 0)
                            + (amountMin !== '' ? 1 : 0)
                            + (amountMax !== '' ? 1 : 0)
                            + (catsFilter.size > 0 ? 1 : 0);
  // Period key shape depends on `range`:
  //   day   → 'YYYY-MM-DD'
  //   month → 'YYYY-MM'
  //   year  → 'YYYY'
  // We initialize to match the default range ('month') and use
  // `periodForRange(new Date(), 'month')` here so the first render
  // produces a shape that filterTxs() understands. Phase 49b: prev fix
  // Phase 63: period/range/catFilter now live in shared filterContext
  // so the header FilterBar (mounted on every page) can drive them.
  const { period, range, catFilter } = useFilter();
  const [autoApplied, setAutoApplied] = useState(null); // transient banner
  // Phase 72: trend chart view toggle — 'timeline' (60/12 buckets in
  // time order, default) or 'byYear' (12-month X axis, 1 line per year).
  // Default flips to 'byYear' on 5y mode since that's the killer view
  // for multi-year data. Resets to default when range changes.
  const [trendView, setTrendView] = useState(range === '5y' ? 'byYear' : 'timeline');
  useEffect(() => { setTrendView(range === '5y' ? 'byYear' : 'timeline'); }, [range]);
  // Phase 73: overlay-metric toggle — 'income' | 'expense' | 'net'.
  // Only meaningful in byYear mode (timeline shows both income+expense
  // solid lines like before). Default 'net' since it's the single most
  // informative signal for year-over-year pattern recognition.
  const [trendMetric, setTrendMetric] = useState('net');
  const [creditCards, setCreditCards] = useState([]); // due reminders
  // Phase 48: goals + transactions (for auto-track mode) are loaded
  // alongside static config — they're cheap (IndexedDB), and we want
  // the widget to render on the very first Dashboard paint.
  const [goals, setGoals] = useState([]);
  const [allTx, setAllTx] = useState([]);
  // Phase 197: recurring rules for the cash flow forecast widget.
  const [recurring, setRecurring] = useState([]);
  // Phase 67: installment plans (loaded for dashboard widget)
  const [installments, setInstallments] = useState([]);
  const nav = useNavigate();

  // Fetch categories once (for filter chips) and settings (currency).
  // Phase 35g: both come from IndexedDB.
  const loadStatic = useCallback(async () => {
    // Phase 109b: auto-apply any due monthly recurring rules BEFORE the
    // dashboard computes its aggregations, so the freshly inserted tx rows
    // are reflected in KPIs / trend / recent-transactions. Mirror of
    // server/recurring.js's per-user 60s cache gate (recurringRepo
    // itself caches; we only call once here).
    // The auto-apply is read FIRST and OUTSIDE the bulk load's `try`,
    // but it is still guarded: `loadStatic` is called from a `useEffect`
    // with no `.catch()`, so a rejection here would escape as an
    // UNHANDLED PROMISE REJECTION.
    let autoAppliedNow = null;
    try {
      autoAppliedNow = await ds.applyRecurringPending();
    } catch (e) {
      console.warn('recurring auto-apply failed', e);
    }
    // The banner state is set in the `finally` of the bulk load below, so
    // every state update from this function lands in ONE React batch.
    // That matters for the reminder effect (card 208-A3): it depends on
    // both `recurring` and `autoApplied`, and two separate batches would
    // run it twice — the second run re-entering the "added" branch with
    // the same rows. (Card 208-B2's once-per-session guard would absorb
    // the repeat, but one batch is simply correct.)
    //
    // The recurring rules are read FIRST and SEPARATELY, so a failure in
    // an UNRELATED read (goals, transactions, settings) cannot suppress
    // the reminder. The reminder is the card's whole point; a broken
    // goals read must not silence it.
    //
    // `recurringRepo.list()` returns the raw Dexie rows, which carry
    // `category_id` but NOT `category_name` — the server JOINs the
    // name, but a rule created locally has never been to the server,
    // so the name would be missing exactly for the users who need it
    // most. Join it LOCALLY (AGENTS.md rule 4 — no server round-trip
    // in the read path).
    //
    // `getCategoriesAll()` rather than `getCategories()`: a rule may
    // point at a DISABLED category, and the name still belongs in
    // the toast. `getCategories()` hides disabled ones.
    let recs = [];
    let allCats = [];
    try {
      [recs, allCats] = await Promise.all([
        // Phase 197: recurring rules for the cash flow forecast widget.
        ds.getRecurring(),
        ds.getCategoriesAll(),
      ]);
    } catch (e) {
      // A failure HERE means the rules are unreadable, so there is
      // nothing to remind about. The rest of the dashboard still loads.
      console.warn('recurring rules load failed', e);
    }
    try {
      const [settings, cats, gs, txs] = await Promise.all([
        ds.getSettings(),
        ds.getCategories(),
        // Phase 48: goals + all transactions for goal auto-tracking.
        // Empty if the user hasn't added any goals yet (GoalsSummary is
        // hidden in that case).
        ds.getGoals(),
        ds.getTransactions({}),
      ]);
      setCurrency(settings.currency || 'HKD');
      setCategories(cats);
      setGoals(gs);
      setAllTx(txs);
    } catch (e) {
      // A bulk read failed. `loadStatic` is called from a `useEffect`
      // with no `.catch()`, so an escaping rejection would be an
      // UNHANDLED PROMISE REJECTION — the failure would be invisible
      // except in the console. Log it instead. (This was true before
      // card 208-A3 too; the card's own regression test could not
      // assert anything about the error path while it was fatal.)
      //
      // The user-visible result is unchanged: `loading` is still true
      // and `data` is still null, so the dashboard keeps rendering its
      // loading state exactly as it did before.
      console.warn('dashboard static load failed', e);
    } finally {
      // The recurring rules are set HERE, outside the bulk read's
      // success path, so an unrelated failure (goals, transactions)
      // cannot leave the rules unset and silence the reminder. They
      // were read in their own guarded block above, so this always has
      // a value.
      setRecurring(withCategoryNames(recs || [], allCats || []));

      // The banner is set in the `finally` for TWO reasons:
      //
      //   1. On the success path it lands in the SAME React batch as the
      //      setters above — they are synchronous continuations of one
      //      `await`, so React 18 batches them together. That keeps the
      //      reminder effect (card 208-A3) from running twice.
      //   2. On the ERROR path — one of the bulk reads rejects — the
      //      banner STILL appears. Setting it before the bulk load (the
      //      pre-change order) had this property; moving it after lost
      //      it, and the independent review of this card caught the
      //      regression. A user whose transactions read fails should
      //      still learn that N recurring rules were auto-added.
      if (autoAppliedNow && autoAppliedNow.length > 0) {
        setAutoApplied(autoAppliedNow.map(a => ({
          ...a,
          // Surface the localized category label for the banner.
          label: a.category_name,
        })));
      }
    }
  }, []);

  // Phase 35g: dashboard aggregations run locally over IndexedDB.
  // computeDashboard() mirrors the server's aggregations in JS. The
  // 6+ server round-trips per dashboard render are now 0.
  // Phase 75: also accepts a filterFn callback so the篩選 drawer's
  // type/amount filters can be applied at fetch time (passed as `txs`
  // override to computeDashboard so KPI/trend/donut all reflect the
  // same narrowed set).
  const loadDashboard = useCallback(async (ym, cid, r, filterFn = null) => {
    try {
      setLoading(true);
      setError(null);
      // If we have advanced filters, fetch the full set + filter client-
      // side, then pass to computeDashboard so its aggregations honor
      // the narrowed slice. Otherwise let computeDashboard fetch its
      // own (limit 5000) — keeps the no-filter path zero overhead.
      let txsOverride = null;
      if (filterFn) {
        const all = await ds.getTransactions({ limit: 5000 });
        txsOverride = all.filter(filterFn);
      }
      const [dash, txs] = await Promise.all([
        computeDashboard({ ym, categoryId: cid, range: r, txs: txsOverride }),
        // Recent 5 transactions (respecting category filter)
        ds.getTransactions(cid ? { category_id: cid, limit: 5 } : { limit: 5 }),
      ]);
      // Map the local-dashboard shape onto what the components expect.
      // The server returned flat keys (income_month, monthly_trend, ...);
      // localDashboard returns nested ({kpi: {...}, monthly_trend: [...], ...}).
      // Wrap to preserve the existing destructuring in this file.
      // Phase 71: also surface monthly_trend_prior for YoY overlay.
      // Phase 72: also surface monthly_trend_overlay for the By Year view.
      const flat = {
        kpi: dash.kpi,
        monthly_trend: dash.monthly_trend,
        monthly_trend_prior: dash.monthly_trend_prior || null,
        monthly_trend_overlay: dash.monthly_trend_overlay || null,
        category_breakdown: dash.category_breakdown,
        budget_alerts: dash.budget_alerts,
        top_categories_30d: dash.top_categories_30d,
        net_worth: dash.net_worth,
      };
      setData(flat);
      setRecent(txs);
      setCreditCards(dash.credit_cards || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatic(); }, [loadStatic]);
  // Phase 75: build a tx-level filter fn from the drawer's state. Returns
  // null when no advanced filters are active so the no-filter path stays
  // a single fetch inside computeDashboard (no extra round-trip).
  const advancedFilterFn = useCallback((t) => {
    if (typeFilter !== 'all' && t.type !== typeFilter) return false;
    const amt = Number(t.amount);
    if (!Number.isFinite(amt)) return true;
    if (amountMin !== '' && amt < Number(amountMin)) return false;
    if (amountMax !== '' && amt > Number(amountMax)) return false;
    // Phase 76b: catsFilter is a multi-select (Set of category ids). When
    // non-empty, only keep txs whose category_id is in the set. We compare
    // against both `local_uuid` and `id` because old txs may lack one.
    if (catsFilter.size > 0) {
      const cid = t.category_id || t.category_local_uuid;
      if (!catsFilter.has(cid)) return false;
    }
    return true;
  }, [typeFilter, amountMin, amountMax, catsFilter]);
  const hasAdvancedFilter = advancedActiveCount > 0;
  useEffect(() => {
    loadDashboard(period, catFilter, range, hasAdvancedFilter ? advancedFilterFn : null);
  }, [loadDashboard, period, catFilter, range, hasAdvancedFilter, advancedFilterFn]);
  // Phase 61: load accounts for the balances widget.
  useEffect(() => {
    let mounted = true;
    db.table('accounts').toArray().then(rows => {
      if (mounted) setAccounts((rows || []).filter(a => !a.deleted));
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  // Phase 67: load installment plans for the installments widget.
  useEffect(() => {
    let mounted = true;
    installmentsRepo.list().then(rows => {
      if (mounted) setInstallments((rows || []).filter(p => !p.deleted));
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  // Pull-to-refresh: bind to viewport when scrolled to top.
  const refresh = useCallback(() => loadDashboard(period, catFilter, range, hasAdvancedFilter ? advancedFilterFn : null), [loadDashboard, period, catFilter, range, hasAdvancedFilter, advancedFilterFn]);
  const ptr = usePullToRefresh(refresh, { threshold: 90 });

  // Phase 67: when an installment's NEXT payment is this month AND the user
  // has opted in to notifications, fire a one-time reminder per (plan, month).
  // Dedup key stored in localStorage so reloads don't re-fire.
  useEffect(() => {
    if (!installments || installments.length === 0) return;
    if (typeof window === 'undefined') return;
    (async () => {
      let enabled = false;
      try {
        const s = await ds.getSettings();
        enabled = s.notification_on_grace === 'true';
      } catch { return; }
      if (!enabled) return;
      try {
        // Import dynamically to avoid loading lib/installments.js synchronously here.
        const { computeInstallmentProgress } = await import('../lib/installments.js');
        await notifications.maybeFireInstallmentReminder({
          plans: installments,
          creditCards: creditCards || [],
          computeProgress: computeInstallmentProgress,
          formatMoney,
          currency,
          t,
          enabled: true,
        });
      } catch (e) { console.warn('installment reminder failed', e); }
    })();
  }, [installments, creditCards]);

  // Phase 31.6: when any credit card has urgency === 'critical' AND the user
  // has opted in (notification_on_grace === 'true'), fire a browser toast.
  // Tag key includes card id + urgency so we re-notify if a card newly crosses
  // into critical. Skip duplicate firings on reload via a ref.
  const notifiedRef = useRef(new Set());
  useEffect(() => {
    if (!creditCards || creditCards.length === 0) return;
    if (typeof window === 'undefined') return;
    fetchSettingsForNotif();
  }, [creditCards]);
  async function fetchSettingsForNotif() {
    let enabled = false;
    try {
      const s = await ds.getSettings();
      enabled = s.notification_on_grace === 'true';
    } catch { return; }
    if (!enabled) return;
    for (const c of creditCards) {
      if (c.urgency !== 'critical') continue;
      // Phase 45: skip already-paid cards. Two ways a card can be
      // "paid in cycle":
      //   1. `paid_in_cycle` is true (computeDueInfo saw a recent
      //      `last_paid_at` within the cycle window).
      //   2. `outstanding_amount <= 0` — the user cleared the balance
      //      but the `last_paid_at` write didn't make it (e.g. payment
      //      comes from a transaction link, not a direct card pay).
      // Either case means "no reminder needed".
      if (c.paid_in_cycle) continue;
      if ((c.outstanding_amount || 0) <= 0) continue;
      // Phase 45: cards from the local-first shape carry `local_uuid`
      // (string UUID), not the server-side numeric `id`. Using `c.id`
      // here produced key = "undefined-…" which meant the dedup
      // Set never matched and the notification re-fired on every
      // Dashboard mount.
      const cardKey = c.local_uuid || c.id;
      const key = `${cardKey}-${c.urgency}-${c.days_until_due ?? 'x'}`;
      if (notifiedRef.current.has(key)) continue;
      notifiedRef.current.add(key);
      notifications.fire({
        title: c.days_until_due === 0
          ? t('dashboard.card.dueTodayTitle', { name: c.name })
          : t('dashboard.card.dueInDaysTitle', { name: c.name, n: c.days_until_due }),
        body: (() => {
          // Phase 109: respect privacy mode in the toast body. The
          // notifications API only accepts strings (Browser Notification
          // spec), so we can't use <Money /> here. Mask the amount with
          // the same digit-count bullet pattern <Money /> uses so the
          // toast shows ••• when privacy is on, real amount otherwise.
          const n = c.outstanding_amount || 0;
          const display = privacyEnabled
            ? '•'.repeat(Math.max(4, Math.abs(Number(n)).toFixed(2).replace(/[^0-9]/g, '').length))
            : formatMoney(n, currency);
          return t('dashboard.card.unpaidHint', { amount: display });
        })(),
        tag: `cc-grace-${cardKey}`,
        url: '/settings',
      });
    }
  }

  // Phase 56: Weekly recap (opt-in via settings.weekly_recap_enabled).
  // Fires once per 7-day window.
  //
  // Phase 205 (lint): this block used to live INSIDE the credit-card loop
  // above and referenced `txs` and `settings` — neither of which is in
  // scope there (they are locals of `loadStatic`). The ReferenceError was
  // swallowed by the surrounding try/catch, so the weekly recap had never
  // fired since it shipped (Phase 56, 2026-08-27), and even with the
  // scope fixed it would only have run for users who own a credit card.
  //
  // It is now a separate effect, driven by the `allTx` state (every
  // transaction, loaded once by `loadStatic`) and the settings read
  // directly from the data service.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!allTx || allTx.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const s = await ds.getSettings();
        if (cancelled) return;
        if (s.weekly_recap_enabled !== 'true') return;
        await notifications.maybeFireWeeklyRecap({
          transactions: allTx,
          currency,
          formatMoney,
          t,
          enabled: true,
        });
      } catch (e) { console.warn('weekly recap failed', e); }
    })();
    return () => { cancelled = true; };
  }, [allTx, currency, t]);

  // ───────────────────────────────────────────────────────────────────────
  // Phase 208 / card 208-A3 — the recurring-transaction reminder.
  //
  // This is the effect that makes Phase 208 user-visible. It fires two
  // DISTINCT notification types (phase spec §5.3):
  //
  //   Type 1 "coming" -> notifications.maybeFireRecurringReminder()
  //                       ONE aggregate toast per ISO week, listing every
  //                       rule that is inside its lead window.
  //   Type 2 "added"  -> notifications.maybeFireAutoAppliedReminder()
  //                       ONE toast when recurring transactions were added
  //                       automatically, at most once per session.
  //
  // WHY THE RULES COME FROM `recurring` STATE, NOT A SERVER ROUND-TRIP
  // -------------------------------------------------------------------
  // `recurring` is set by `loadStatic` from `ds.getRecurring()`, which
  // reads the LOCAL Dexie store. AGENTS.md rule 4: local-first means
  // local-first — the UI must not depend on a server round-trip for
  // reads. A GET /api/recurring here would put the notification on the
  // network path, and make the reminder fire late — or not at all — on a
  // flaky connection.
  //
  // WHY THE ORDERING IS SAFE
  // ------------------------
  // `loadStatic` calls `applyRecurringPending()` FIRST and only then
  // fetches the rules and sets `recurring`. This effect depends on
  // `recurring`, so it cannot run before the auto-apply has happened: a
  // rule that was just auto-applied this session has already landed in
  // the ledger before the "coming" reminder is evaluated.
  // (`applyPending` is behind a 60s cache, so the "added" notice can
  // only fire once per session anyway — phase spec §5.3.)
  //
  // WHY THE OPT-IN GOES THROUGH `isRecurringReminderEnabled`
  // -------------------------------------------------------
  // The global master switch is `recurring_reminder_enabled`
  // (phase spec §4.3). Card 208-B4 owns its semantics in
  // `recurringReminderSetting.js`; this effect uses that helper rather
  // than re-implementing the comparison, so the Settings toggle
  // (card 208-A2) and this effect cannot drift apart. An absent key
  // means OFF — the feature stays silent until the user opts in.
  //
  // WHY BOTH FIRES SHARE ONE EFFECT, AND WHY IT KEEPS NO REF
  // ----------------------------------------------------------
  // They are two views of the same dashboard load, and card 208-B2's two
  // helpers each own their own dedup state (the ISO-week key in
  // localStorage, and a module-level once-per-session flag). A third
  // dedup layer here would be a fourth source of truth for "have we
  // already told the user" — so this effect deliberately keeps NO ref
  // of its own. It calls the two helpers and lets them decide.
  // ───────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // The global opt-in gates BOTH types. Read it before anything else
    // so a disabled feature never even touches the rules.
    let cancelled = false;
    (async () => {
      try {
        const settings = await ds.getSettings();
        if (cancelled) return;
        if (!isRecurringReminderEnabled(settings)) return;

        // Type 1 — "coming". `recurring` may be empty (the user has no
        // rules at all); card 208-B2's helper handles that and returns
        // `no_due_rules`.
        //
        // The rules that were JUST auto-applied are excluded: they are
        // announced by the "added" toast instead (phase spec §5.2 chose
        // (b) — notify a just-applied rule — but as a SEPARATE type, not
        // IN ADDITION to the "coming" nudge). Without this filter the
        // same rule is announced twice, and the "coming" toast also
        // burns the ISO-week dedup key, silencing a genuinely future
        // rule for the rest of the week.
        const comingRules = withoutJustApplied(recurring, autoApplied);
        if (comingRules && comingRules.length > 0) {
          await notifications.maybeFireRecurringReminder({
            rules: comingRules,
            currency,
            formatMoney,
            t,
            enabled: true,
          });
        }

        // Type 2 — "added". `autoApplied` is set by `loadStatic` when
        // `applyPending()` inserted something. It is never cleared, so
        // this branch is entered at most once per session in practice —
        // and card 208-B2's `maybeFireAutoAppliedReminder` owns the
        // once-per-session guard as well (two independent guards, so
        // neither alone is load-bearing).
        if (autoApplied && autoApplied.length > 0) {
          await notifications.maybeFireAutoAppliedReminder({
            applied: autoApplied,
            currency,
            formatMoney,
            t,
            enabled: true,
          });
        }
      } catch (e) { console.warn('recurring reminder failed', e); }
    })();
    return () => { cancelled = true; };
  }, [recurring, autoApplied, currency, t]);

  // Cross-page tx-saved: re-load current view via the shared sync store.
  // (Phase 31.10 — replaces the window.addEventListener pattern. The hook
  // keeps listening to the legacy event as well, so unmigrated pages still
  // work during the transition.)
  useTxSync(() => loadDashboard(period, catFilter, range, hasAdvancedFilter ? advancedFilterFn : null), [loadDashboard, period, catFilter, range, hasAdvancedFilter, advancedFilterFn]);

  if (loading && !data) {
    return <div className="p-6 text-center text-slate-400 dark:text-slate-500 py-12">{t('common.loadingDots')}</div>;
  }
  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300 p-3 rounded-md text-sm">{error}</div>
      </div>
    );
  }
  if (!data) return null;

  const { kpi, monthly_trend, monthly_trend_overlay, category_breakdown, budget_alerts, net_worth } = data;
  const isYear = range === 'year';
  const isDay = range === 'day';
  const is5y = range === '5y';
  // Phase 35g: catFilter is now a local_uuid (from categoryRepo.list),
  // not a server numeric id.
  const activeCat = catFilter ? categories.find(c => (c.local_uuid || c.id) === catFilter) : null;
  // Period key shape depends on the active range. After Phase 49b, the
  // shape is normalized at every boundary (initial load + range-toggle +
  // previous/next navigation), so display just slices appropriately.
  // Phase 77f: year mode now also computes a real vs-last-year delta
  // (was hardcoded to null/0 before). 5y mode still hides the delta —
  // comparing 5 years of totals to the prior 5 years is too noisy to
  // surface as a single percentage.
  const deltaIncome  = is5y ? null : kpi.vs_last_month.income;
  const deltaExpense = is5y ? null : kpi.vs_last_month.expense;
  // Savings rate delta: current rate minus prior rate, in percentage
  // points (not percent). Phase 77g: prior_savings_rate is surfaced by
  // computeKpi for all ranges (day/month/year/5y); null when prior
  // income is 0 (no comparison possible).
  const priorSavingsRate = kpi.vs_last_month.savings_rate;
  const deltaSavings = (() => {
    if (is5y) return null;
    if (priorSavingsRate == null || kpi.income_month <= 0) return null;
    return (kpi.savings_rate - priorSavingsRate) * 100; // pp
  })();
  // Compare label: 昨日 for day, 上月 for month, 去年 for year.
  const compareLabel = isDay ? t('dashboard.vsYesterday')
                    : isYear ? t('dashboard.vsLastYear')
                    : t('dashboard.vsLast');

  return (
    <div
      className="p-4 pb-32 relative"
      onTouchStart={ptr.onTouchStart}
      onTouchMove={ptr.onTouchMove}
      onTouchEnd={ptr.onTouchEnd}
    >
      {/* Pull-to-refresh indicator */}
      <div
        className="flex items-center justify-center overflow-hidden transition-all duration-200"
        style={{ height: ptr.pulling || ptr.refreshing ? Math.max(28, ptr.distance) : 0 }}
        aria-hidden="true"
      >
        <div className={`text-xs text-slate-500 dark:text-slate-400 ${ptr.refreshing ? 'animate-spin' : ''}`}>
          {ptr.refreshing ? t('common.refresh.refreshing') : ptr.pulling ? t('common.refresh.release') : ''}
        </div>
      </div>

      {/* Auto-applied recurring notice */}
      {autoApplied && autoApplied.length > 0 && (
        <div className="bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-700 rounded-md px-3 py-2 mb-3 text-xs text-emerald-700 dark:text-emerald-200">
          <div className="font-medium mb-1">{t('dashboard.autoAppliedFmt', { n: autoApplied.length })}</div>
          <ul className="space-y-0.5">
            {autoApplied.map((a, i) => (
               <li key={i} className="tabular-nums">
                {a.type === 'income' ? '+' : '-'}<Money amount={a.amount} currency={currency} />
                {' '}<b>{categoryLabel({ name: a.category_name }, t)}</b>
              </li>
            ))}
          </ul>
        </div>
      )}



      {/* Phase 76b:篩選 (filter) button portaled into TopBar's right
          slot so the header stays single-line at all viewports. The
          button visually appears in the header row alongside the brand
          + FilterBar; the drawer modal still renders here in Dashboard. */}
      {/* Phase 94: 篩選 filter button portaled into the TopBar right-
          cluster slot via React Context (lib/topbar-slot.jsx). Was an
          inline IIFE before; migrated for consistency with
          Transactions and to eliminate the createPortal race. */}
      {topbarSlot && ReactDOM.createPortal(
        <button
          data-testid="dash-open-filters"
          onClick={() => setFilterDrawerOpen(true)}
          aria-pressed={filterDrawerOpen}
          className={[
            'inline-flex items-center gap-1 px-2 sm:px-2.5 h-8 rounded-md text-xs font-medium min-h-[32px]',
            hasAdvancedFilter
              ? 'bg-brand-600 text-white'
              : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600',
          ].join(' ')}
          aria-label={t('dashboard.filter.openFilters')}
        >
          <span className="w-3.5 h-3.5 inline-flex" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                 className="w-full h-full">
              <path d="M3 5h18M6 12h12M10 19h4" />
            </svg>
          </span>
          <span className="hidden sm:inline">{t('dashboard.filter.openFilters')}</span>
          {hasAdvancedFilter && (
            <span
              className="ml-0.5 px-1.5 rounded-full bg-white/20 text-[10px] tabular-nums"
              data-testid="dash-filter-count"
            >
              +{advancedActiveCount}
            </span>
          )}
        </button>,
        topbarSlot
      )}

      {/* Phase 50: Net Worth hero card — cumulative balance (income - expense).
          Distinct from the 2x2 KPI grid below which is *period-comparison*.
          Net worth is a snapshot of your financial health; render it as
          a hero block above the period KPIs so users see their overall
          position first, then the month-to-date breakdown. */}
      <div
        className="bg-gradient-to-br from-brand-50 to-white dark:from-slate-800 dark:to-slate-800 rounded-xl border border-brand-100 dark:border-slate-700 p-4 mb-3"
        data-testid="networth-hero"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
            <span aria-hidden="true">💼</span>
            <span>{t('dashboard.networth')}</span>
          </div>
          {/* Phase 49r.3: positive / negative label badge for at-a-glance meaning. */}
          {net_worth && (
            <span className={[
              'text-[10px] font-medium px-2 py-0.5 rounded-full',
              net_worth.balance >= 0
                ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
            ].join(' ')}>
              {net_worth.balance >= 0 ? t('dashboard.networthPositive') : t('dashboard.networthNegative')}
            </span>
          )}
        </div>
        <div className={[
          'text-2xl sm:text-3xl font-bold tabular-nums mt-1',
          (net_worth && net_worth.balance >= 0)
            ? 'text-brand-600 dark:text-brand-400'
            : 'text-red-600 dark:text-red-400',
        ].join(' ')}>
          <Money amount={net_worth ? net_worth.balance : 0} currency={currency} />
        </div>
        <div className="text-xs text-slate-400 dark:text-slate-500 mt-1">
          {t('dashboard.networthHelper')}
        </div>
      </div>

      {/* Phase 53 (was finding #1): Net Worth value was rendered 3× on Dashboard
          (hero + 淨值 KPI + 結餘 Balance card). Now the KPI grid is just
          income / expense / savings — orthogonal period data, no
          redundant net. The 淨值 metric lives in the hero card above. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <KpiCard
          label={is5y ? t('dashboard.kpi.income5y') : isYear ? t('dashboard.kpi.incomeYear') : isDay ? t('dashboard.kpi.incomeDay') : t('dashboard.income')}
          value={<Money amount={kpi.income_month} currency={currency} />}
          delta={deltaIncome}
          compareLabel={compareLabel}
          accent="income"
          icon="💰"
        />
        <KpiCard
          label={is5y ? t('dashboard.kpi.expense5y') : isYear ? t('dashboard.kpi.expenseYear') : isDay ? t('dashboard.kpi.expenseDay') : t('dashboard.expense')}
          value={<Money amount={kpi.expense_month} currency={currency} />}
          delta={deltaExpense}
          compareLabel={compareLabel}
          accent="expense"
          icon="💸"
        />
        <KpiCard
          label={t('dashboard.savings')}
          value={formatPercent(kpi.savings_rate, 2)}
          // Phase 77g: show pp delta vs prior period when available.
          // Compare label reuses the same 上月 / 去年 / 昨日 string.
          delta={deltaSavings}
          compareLabel={compareLabel}
          deltaIsPp={true}
          accent={kpi.savings_rate >= 0 ? 'income' : 'expense'}
          icon="🎯"
        />
      </div>

      {/* Phase 77: removed the inline networth-summary line. The hero
          card already shows the breakdown (total_income · total_expense),
          so this row was redundant — repeating the same numbers below
          the KPI cards. The networth-hero card remains the single
          source of truth for "where my money is". */}

      {/* Trend chart */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-3 sm:p-4 mb-4">
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          {/* Phase 73: chart title shows mode + range. In byYear mode,
              drop the "(60 個月)" / "(12 個月)" suffix — X axis is always
              12 months so the count is implicit. */}
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            {trendView === 'byYear'
              ? t('dashboard.trend.viewByYear')
              : is5y ? t('dashboard.rangeLabel.fiveYear')
              : isYear ? t('dashboard.rangeLabel.year')
              : isDay ? t('dashboard.rangeLabel.week')
              : t('dashboard.rangeLabel.halfYear')}
            {activeCat ? ` · ${categoryLabel(activeCat, t)}` : ''}
          </h3>
          {/* Phase 72: Timeline / By Year toggle, only in year + 5y mode
              (those are the only ranges where multi-year comparison makes
              sense). month + day ranges always show the timeline view. */}
          {(isYear || is5y) && monthly_trend_overlay && (
            <div
              className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden shrink-0"
              role="group"
              aria-label={t('dashboard.trend.viewLabel')}
              data-testid="trend-view-toggle"
            >
              {[
                { k: 'timeline', label: t('dashboard.trend.viewTimeline') },
                { k: 'byYear',   label: t('dashboard.trend.viewByYear') },
              ].map(s => (
                <button
                  key={s.k}
                  data-testid={`trend-view-${s.k}`}
                  aria-pressed={trendView === s.k}
                  onClick={() => setTrendView(s.k)}
                  className={[
                    'px-2.5 py-1 text-xs min-h-[32px] min-w-[64px]',
                    trendView === s.k
                      ? 'bg-brand-600 text-white'
                      : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700',
                  ].join(' ')}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>
        {/* Phase 73: in byYear mode, also show the income / expense / net
            metric selector. Smaller, sits under the view toggle. */}
        {(isYear || is5y) && monthly_trend_overlay && trendView === 'byYear' && (
          <div className="flex items-center gap-2 mb-2" data-testid="trend-metric-row">
            <span className="text-xs text-slate-500 dark:text-slate-400">{t('dashboard.trend.metricLabel')}</span>
            <div
              className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden shrink-0"
              role="group"
              aria-label={t('dashboard.trend.metricLabel')}
            >
              {[
                { k: 'income',  label: t('dashboard.income') },
                { k: 'expense', label: t('dashboard.expense') },
                { k: 'net',     label: t('dashboard.net') },
              ].map(s => (
                <button
                  key={s.k}
                  data-testid={`trend-metric-${s.k}`}
                  aria-pressed={trendMetric === s.k}
                  onClick={() => setTrendMetric(s.k)}
                  className={[
                    'px-2 py-0.5 text-[11px] min-h-[28px]',
                    trendMetric === s.k
                      ? 'bg-brand-600 text-white'
                      : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700',
                  ].join(' ')}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {/* Phase 73: in byYear mode the chart switches to a 12-month X
            axis with one year-colored line per year for the selected
            metric. Timeline mode keeps the original 60/12-bucket view. */}
        {trendView === 'byYear' && monthly_trend_overlay ? (
          <DashboardTrendChart currency={currency} overlay={monthly_trend_overlay} metric={trendMetric} />
        ) : (
          <DashboardTrendChart data={monthly_trend} currency={currency} />
        )}
      </div>

      {/* Category donut — hidden when single category is selected (no useful breakdown) */}
      {!activeCat && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-3 sm:p-4 mb-4">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">{t('dashboard.categoryBreakdown')}</h3>
          <DashboardCategoryDonut data={category_breakdown} currency={currency} />
        </div>
      )}

      {/* Budget alerts — show only when no filter (with filter, single alert row is noisy) */}
      {!activeCat && budget_alerts && budget_alerts.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-3 sm:p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">{t('budgets.alerts')}{(isYear || is5y) ? `(${is5y ? t('dashboard.range.5y') : t('dashboard.range.year')})` : ''}</h3>
            <button
              onClick={() => nav('/budgets')}
              className="text-xs text-brand-600 dark:text-brand-300 hover:underline min-h-[44px] px-2"
            >
              {t('common.manage')} →
            </button>
          </div>
          <BudgetAlerts alerts={budget_alerts} currency={currency} />
        </div>
      )}

      {/* Phase 48: Savings goals summary. Hidden entirely when there are
          no goals. Hidden when a category filter is active (single-category
          view would be misleading for cross-cat goals).

          Phase 49b: goals track all-time cumulative progress toward a
          target (it's how savings goals work), so passing `allTx` is
          correct — capping the tx list to a period would mislead users
          into thinking the goal slowed/accelerated in the active month.
          The rest of the Dashboard (KPI / trend / budget alerts) IS
          period-bounded because those are period-activity metrics. */}
      {!activeCat && goals.length > 0 && (
        <GoalsSummary goals={goals} transactions={allTx} currency={currency} />
      )}

      {/* Phase 197: Cash flow forecast — 30-day projection from recurring rules.
          Mounts unconditionally; the widget itself shows the empty-state CTA
          when the user has 0 active recurring rules (so they get pointed at
          the recurring settings page, just like GoalsSummary's empty state). */}
      {!activeCat && (
        <div className="mb-4">
          <CashFlowForecast
            rules={recurring}
            currentBalance={net_worth ? net_worth.balance : 0}
            today={new Date()}
            currency={currency}
          />
        </div>
      )}

      {/* Credit cards — Phase 49m: render ALL cards always (paid + unpaid).
          Previously filtered to unpaid-only which made cards vanish after
          Mark Paid, leaving users no way to navigate to CardDetail. Now paid
          cards show as muted "✅ Paid on {date}" rows, still clickable.
          Sort: unpaid cards first (urgent → ok), then paid cards (most recent first). */}
      {!activeCat && creditCards && creditCards.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-3 sm:p-4 mb-4" data-testid="dashboard-card-reminder">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">{t('dashboard.cardReminder')} 💳</h3>
            <button
              onClick={() => nav('/cards')}
              className="text-xs text-brand-600 dark:text-brand-300 hover:underline min-h-[44px] px-2"
            >
              {t('common.manage')} →
            </button>
          </div>
          <ul className="space-y-2">
            {[...creditCards]
              .sort((a, b) => {
                // Unpaid first (most urgent first), then paid (most recent paid first).
                const aPaid = a.paid_in_cycle ? 1 : 0;
                const bPaid = b.paid_in_cycle ? 1 : 0;
                if (aPaid !== bPaid) return aPaid - bPaid;
                if (!aPaid) return (a.days_until_due ?? 999) - (b.days_until_due ?? 999);
                // Both paid: most recently paid first.
                return (b.last_paid_at || '').localeCompare(a.last_paid_at || '');
              })
              .map(c => {
                const overdue = c.is_overdue;
                const isCritical = c.urgency === 'critical' || overdue;
                const graceSuffix = c.grace_days_remaining != null
                  ? t('dashboard.card.graceSuffix', { n: c.grace_days_remaining })
                  : '';
                const overdueDays = overdue && c.cycle_end_iso
                  ? Math.max(1, Math.round(
                      (Date.now() - new Date(c.cycle_end_iso + 'T00:00:00').getTime()) / 86400000
                    ))
                  : 0;
                const isPaid = c.paid_in_cycle;
                return (
                <li key={c.local_uuid || c.id} className="list-none">
                  <button
                    type="button"
                    onClick={() => nav(`/cards/${c.local_uuid || c.id}`)}
                    aria-label={t('cardDetail.openCard', { name: c.name })}
                    className={[
                      'w-full text-left flex items-center justify-between p-2.5 rounded-lg border cursor-pointer hover:opacity-80 active:opacity-60 transition-opacity',
                      isPaid
                        ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800/60'
                        : isCritical
                          ? 'bg-red-50 dark:bg-red-900/40 border-red-200 dark:border-red-800'
                          : 'bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800',
                    ].join(' ')}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span aria-hidden="true">💳</span>
                        <span className="font-medium text-sm text-slate-800 dark:text-slate-100 truncate">{c.name}</span>
                        {isPaid && (
                          <span aria-hidden="true" className="text-xs text-emerald-600 dark:text-emerald-400">✅</span>
                        )}
                      </div>
                      <div className={[
                        'text-xs mt-0.5 tabular-nums',
                        isPaid
                          ? 'text-emerald-700 dark:text-emerald-300'
                          : isCritical
                            ? 'text-red-700 dark:text-red-300'
                            : 'text-amber-700 dark:text-amber-300',
                      ].join(' ')}>
                        {isPaid
                          ? t('dashboard.card.paidOn', { date: c.last_paid_at || '—' })
                          : overdue
                            ? t('dashboard.card.overdue', { n: overdueDays, date: c.cycle_end_iso })
                            : c.days_until_due === 0
                              ? t('dashboard.card.dueToday', { date: c.next_due_iso }) + graceSuffix
                              : c.days_until_due === 1
                                ? t('dashboard.card.dueTomorrow', { date: c.next_due_iso }) + graceSuffix
                                : t('dashboard.card.dueInDaysFmt', { n: c.days_until_due, date: c.next_due_iso }) + graceSuffix}
                      </div>
                    </div>
                    <div className="ml-2 text-right">
                      <div className={[
                        'text-sm font-semibold tabular-nums',
                        isPaid
                          ? 'text-emerald-700 dark:text-emerald-300'
                          : isCritical
                            ? 'text-red-700 dark:text-red-300'
                            : 'text-amber-700 dark:text-amber-300',
                      ].join(' ')}>
                        <Money amount={c.outstanding_amount} currency={currency} />
                      </div>
                    </div>
                  </button>
                </li>
                );
              })}
          </ul>
        </div>
      )}

      {/* Phase 67: Installments widget — only shown if user has active installment plans */}
      {installments && installments.length > 0 && (() => {
        const cardsByUuid = {};
        for (const c of (creditCards || [])) cardsByUuid[c.local_uuid] = c;
        return (
          <div className="mb-4" data-testid="dashboard-installments">
            <InstallmentsWidget
              plans={installments}
              cardsByUuid={cardsByUuid}
              mode="dashboard"
              empty={t('installments.empty')}
            />
          </div>
        );
      })()}

      {/* Recent transactions */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-3 sm:p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">{t('dashboard.recentTransactions')}{activeCat ? ` · ${categoryLabel(activeCat, t)}` : ''}</h3>
          <button
            onClick={() => nav('/transactions')}
            className="text-xs text-brand-600 dark:text-brand-300 hover:underline min-h-[44px] px-2"
          >
            {t('tx.seeAll')} →
          </button>
        </div>
      {/* Phase 61: Account balances */}
      <AccountBalancesWidget transactions={allTx} accounts={accounts} currency={currency} />


        <RecentTransactions
          transactions={recent}
          currency={currency}
          limit={5}
          onViewAll={() => nav('/transactions')}
        />
      </div>

      {/* Phase 75: filter drawer — advanced filters (type + amount range)
          that complement the always-visible FilterBar (range/period/
          category). Mirrors Transactions' tx-filter-drawer pattern. */}
      {filterDrawerOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 flex items-end sm:items-center justify-center"
          onClick={(e) => { if (e.target === e.currentTarget) setFilterDrawerOpen(false); }}
          data-testid="dash-filter-drawer"
        >
          <div className="bg-white dark:bg-slate-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">
                {t('dashboard.filter.title')}
              </h3>
              <button
                onClick={() => setFilterDrawerOpen(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 min-h-[44px] px-3"
                aria-label={t('common.close')}
              >
                ✕
              </button>
            </div>

            {/* Type filter */}
            <div>
              <div className="text-xs text-slate-500 dark:text-slate-400 mb-1.5">
                {t('dashboard.filter.typeLabel')}
              </div>
              <div className="flex gap-1.5">
                {[
                  { k: 'all',     label: t('common.all') },
                  { k: 'expense', label: t('dashboard.expense') },
                  { k: 'income',  label: t('dashboard.income') },
                ].map(f => (
                  <button
                    key={f.k}
                    data-testid={`dash-filter-type-${f.k}`}
                    onClick={() => setTypeFilter(f.k)}
                    className={[
                      'px-3 py-1.5 rounded-full text-sm min-h-[36px]',
                      typeFilter === f.k
                        ? 'bg-brand-600 text-white'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200',
                    ].join(' ')}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Categories multi-select (Phase 76b). Chips toggle in/out
                of the catsFilter Set. The FilterBar's chip row no longer
                renders — categories live here as part of the篩選 drawer. */}
            <div>
              <div className="text-xs text-slate-500 dark:text-slate-400 mb-1.5 flex items-center justify-between">
                <span>{t('dashboard.filter.categoriesLabel')}</span>
                {catsFilter.size > 0 && (
                  <button
                    onClick={() => setCatsFilter(new Set())}
                    className="text-brand-600 dark:text-brand-300 hover:underline text-[11px] min-h-[24px]"
                    data-testid="dash-filter-cats-clear"
                  >
                    {t('common.clear')}
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                {categories.map(c => {
                  const cid = c.local_uuid || c.id;
                  const isActive = catsFilter.has(cid);
                  return (
                    <button
                      key={cid}
                      data-testid={`dash-filter-cat-${cid}`}
                      data-active={isActive ? 'true' : 'false'}
                      onClick={() => {
                        setCatsFilter(prev => {
                          const next = new Set(prev);
                          if (next.has(cid)) next.delete(cid);
                          else next.add(cid);
                          return next;
                        });
                      }}
                      className={[
                        'shrink-0 px-2 py-1 rounded-full text-xs min-h-[28px] flex items-center gap-1',
                        isActive
                          ? 'text-white'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200',
                      ].join(' ')}
                      style={isActive ? { backgroundColor: c.color || '#475569' } : undefined}
                    >
                      <span>{c.icon}</span>
                      <span>{c.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Amount range filter */}
            <div>
              <div className="text-xs text-slate-500 dark:text-slate-400 mb-1.5">
                {t('dashboard.filter.amountLabel')}
              </div>
              <div className="flex items-center gap-2 min-w-0">
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  value={amountMin}
                  onChange={e => setAmountMin(e.target.value)}
                  placeholder={t('dashboard.filter.amountMin')}
                  data-testid="dash-filter-amount-min"
                  className="min-w-0 flex-1 px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 rounded-lg text-sm placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-brand-500 outline-none min-h-[40px] tabular-nums"
                />
                <span className="text-slate-400 dark:text-slate-500 shrink-0" aria-hidden="true">–</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  value={amountMax}
                  onChange={e => setAmountMax(e.target.value)}
                  placeholder={t('dashboard.filter.amountMax')}
                  data-testid="dash-filter-amount-max"
                  className="min-w-0 flex-1 px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 rounded-lg text-sm placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-brand-500 outline-none min-h-[40px] tabular-nums"
                />
              </div>
            </div>

            {/* Sticky footer: Reset + Apply */}
            <div className="sticky bottom-0 -mx-4 px-4 pt-3 pb-1 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 flex gap-2">
              <button
                onClick={() => {
                  setTypeFilter('all');
                  setAmountMin('');
                  setAmountMax('');
                  setCatsFilter(new Set());
                }}
                className="flex-1 px-3 py-2 rounded-lg text-sm text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 min-h-[44px]"
                data-testid="dash-filter-reset"
              >
                {t('transactions.resetFilters')}
              </button>
              <button
                onClick={() => setFilterDrawerOpen(false)}
                className="flex-1 px-3 py-2 rounded-lg text-sm font-medium bg-brand-600 hover:bg-brand-700 text-white min-h-[44px]"
                data-testid="dash-filter-apply"
              >
                {t('transactions.applyFilters')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
